import { ArrowLeft, ArrowRight, ShieldCheck } from "lucide-react";
import { Link, Navigate } from "react-router-dom";
import SEO from "@/components/SEO";
import {
  findHaloEducationArticle,
  haloEducationArticles,
} from "@/config/haloEducation";

export default function HaloEducationArticle({ slug }) {
  const article = findHaloEducationArticle(slug);

  if (!article) {
    return <Navigate to="/halo" replace />;
  }

  const related = haloEducationArticles.find((candidate) => candidate.slug !== article.slug);

  return (
    <article className="bg-white text-ink-900">
      <SEO
        title={`${article.title} — Halo SuperVPN by LightningFlow`}
        description={article.seoDescription}
        canonicalPath={`/halo/learn/${article.slug}`}
      />

      <header className="relative overflow-hidden bg-[#031124] text-white">
        <div className="absolute inset-0 lf-grid-bg-faint opacity-20" />
        <div className="lf-container relative py-20 md:py-28">
          <Link
            className="inline-flex items-center gap-2 text-sm font-medium text-cyan-200 hover:text-white"
            to="/halo"
            data-testid="halo-education-back"
          >
            <ArrowLeft className="h-4 w-4" /> Back to Halo SuperVPN
          </Link>
          <div className="mt-12">
            <div className="lf-eyebrow text-cyan-200">{article.eyebrow}</div>
          </div>
          <h1 className="mt-5 max-w-4xl break-words font-display text-4xl font-black leading-[.96] tracking-[-.035em] sm:text-5xl md:text-7xl">
            {article.title}
          </h1>
          <p className="mt-7 max-w-3xl text-lg leading-relaxed text-slate-200 md:text-xl">
            {article.summary}
          </p>
        </div>
      </header>

      <div className="lf-container grid gap-12 py-16 md:py-24 lg:grid-cols-[1fr_320px]">
        <div className="space-y-12">
          {article.sections.map((section, index) => (
            <section key={section.heading} className="border-t border-ink-100 pt-8">
              <div className="font-mono text-[10px] uppercase tracking-[.18em] text-ink-400">
                0{index + 1}
              </div>
              <h2 className="mt-4 font-display text-3xl font-black">{section.heading}</h2>
              <p className="mt-5 max-w-3xl text-lg leading-relaxed text-ink-600">{section.body}</p>
            </section>
          ))}
        </div>

        <aside className="h-fit border border-ink-900 bg-ink-900 p-7 text-white lg:sticky lg:top-28">
          <ShieldCheck className="h-6 w-6 text-cyan-300" strokeWidth={1.7} />
          <div className="mt-5 font-mono text-[10px] uppercase tracking-[.18em] text-cyan-200">
            Built into Halo SuperVPN
          </div>
          <p className="mt-4 text-sm leading-relaxed text-slate-200">{article.spotlight}</p>
          <Link
            className="lf-btn mt-6 border border-white bg-white text-ink-900 hover:border-cyan-300 hover:bg-cyan-300"
            to="/security"
            data-testid="halo-education-security"
          >
            Security &amp; Trust
          </Link>
        </aside>
      </div>

      <section className="border-y border-ink-100 bg-ink-50">
        <div className="lf-container grid gap-8 py-12 md:grid-cols-[1fr_auto] md:items-center">
          <div>
            <div className="lf-eyebrow">Continue learning</div>
            <h2 className="mt-3 font-display text-3xl font-black">{related.title}</h2>
          </div>
          <Link
            className="lf-btn-primary"
            to={`/halo/learn/${related.slug}`}
            data-testid="halo-education-related"
          >
            Read next <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </article>
  );
}
